<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="goBack">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-motorcycle me-2 text-primary"></i>
            {{ $t('gestioneAsporto') || 'Gestione Asporto & Delivery' }}
          </h1>
          <p class="sci-subtitle">Monitoraggio in tempo reale degli ordini da asporto, stati di preparazione e ritiri</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button class="sci-btn sci-btn-primary" @click="goToCreateOrder">
          <i class="fas fa-plus me-1"></i>
          <span>{{ $t('aggiungi_piatti') || 'Nuovo Ordine Asporto' }}</span>
        </button>
        <button class="sci-btn sci-btn-secondary" @click="showSettings = true">
          <i class="fas fa-cog me-1"></i>
          <span>{{ $t('prenotazioniSetting') || 'Orari Asporto' }}</span>
        </button>
        <button class="sci-btn sci-btn-secondary" @click="fetchOrdini" :disabled="isLoading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          <span>{{ $t('aggiorna') || 'Aggiorna' }}</span>
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="sci-main-content">
      <!-- Toolbar Filters & KPIs -->
      <div class="sci-toolbar-card">
        <!-- Status Filter Pills -->
        <div class="status-filters">
          <button 
            class="status-pill" 
            :class="{ active: filtroStato === '' }"
            @click="setStatusFilter('')"
          >
            Tutti ({{ ordini.length }})
          </button>
          <button 
            class="status-pill status-pill-pagato" 
            :class="{ active: filtroStato === 'pagato' }"
            @click="setStatusFilter('pagato')"
          >
            <i class="fas fa-bell me-1"></i>
            {{ $t('nuovi_pagati') || 'Nuovi' }} ({{ countByStatus('pagato') }})
          </button>
          <button 
            class="status-pill status-pill-prep" 
            :class="{ active: filtroStato === 'in_preparazione' }"
            @click="setStatusFilter('in_preparazione')"
          >
            <i class="fas fa-fire me-1"></i>
            {{ $t('preparazione') || 'In Preparazione' }} ({{ countByStatus('in_preparazione') }})
          </button>
          <button 
            class="status-pill status-pill-pronto" 
            :class="{ active: filtroStato === 'pronto' }"
            @click="setStatusFilter('pronto')"
          >
            <i class="fas fa-check me-1"></i>
            {{ $t('pronto') || 'Pronti' }} ({{ countByStatus('pronto') }})
          </button>
          <button 
            class="status-pill status-pill-consegnato" 
            :class="{ active: filtroStato === 'consegnato' }"
            @click="setStatusFilter('consegnato')"
          >
            <i class="fas fa-check-double me-1"></i>
            {{ $t('consegnato') || 'Consegnati' }} ({{ countByStatus('consegnato') }})
          </button>
        </div>

        <!-- Date & Search Toolbar Right -->
        <div class="toolbar-right">
          <div class="date-input-group">
            <i class="fas fa-calendar-alt date-icon"></i>
            <input 
              type="date" 
              id="dataFiltro" 
              v-model="dataFiltro" 
              @change="fetchOrdini"
              class="sci-date-input"
            />
          </div>
          <div class="search-input-group">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Cerca cliente o telefono..."
              class="sci-search-input"
            />
            <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''">&times;</button>
          </div>
        </div>
      </div>

      <!-- Orders List / Grid -->
      <section v-if="filteredOrdini.length" class="orders-grid">
        <div 
          v-for="ordine in filteredOrdini" 
          :key="ordine._id" 
          class="sci-card order-card" 
          :class="'card-status-' + ordine.stato"
        >
          <!-- Card Header -->
          <div class="order-card-header">
            <div class="order-customer">
              <h3 class="customer-name">{{ ordine.nome }}</h3>
              <div class="customer-contacts">
                <span v-if="ordine.telefono" class="contact-pill">
                  <i class="fas fa-phone-alt me-1"></i> {{ ordine.telefono }}
                </span>
                <span v-if="ordine.email || ordine.contatto" class="contact-pill email-pill">
                  <i class="fas fa-envelope me-1"></i> {{ ordine.email || ordine.contatto }}
                </span>
              </div>
            </div>

            <!-- Badges -->
            <div class="order-header-badges">
              <span v-if="ordine.isStaff" class="badge-staff">
                <i class="fas fa-user-shield me-1"></i> STAFF
              </span>
              <span v-if="!ordine.pagato" class="badge-unpaid">
                <i class="fas fa-exclamation-triangle me-1"></i> DA PAGARE
              </span>
              <span class="order-timestamp">
                <i class="far fa-clock me-1"></i> {{ formatDate(ordine.tm) }}
              </span>
            </div>
          </div>

          <!-- Pickup Time Badge (Crucial Info) -->
          <div class="pickup-time-banner" v-if="ordine.dataRitiro">
            <i class="fas fa-calendar-check me-2"></i>
            <span class="pickup-label">Ritiro previsto:</span>
            <strong class="pickup-datetime">{{ formatDataRitiro(ordine.dataRitiro) }} alle {{ ordine.oraRitiro }}</strong>
          </div>

          <!-- Items Ordered -->
          <div class="order-items-box">
            <div v-for="(item, idx) in ordine.ordinazione" :key="idx" class="item-entry">
              <div class="item-main-row">
                <span class="item-qty font-monospace">{{ item.quantita }}x</span>
                <span class="item-title">{{ item.piatto }}</span>
              </div>
              <div class="item-extras" v-if="item.aggiunta && item.aggiunta.length">
                <span class="extra-add">
                  <i class="fas fa-plus-circle me-1"></i> {{ formatVarianti(item.aggiunta) }}
                </span>
              </div>
              <div class="item-extras" v-if="item.senza && item.senza.length">
                <span class="extra-rem">
                  <i class="fas fa-minus-circle me-1"></i> {{ formatVarianti(item.senza) }}
                </span>
              </div>
              <div class="item-note" v-if="item.nota">
                <i class="fas fa-sticky-note me-1"></i> {{ item.nota }}
              </div>
            </div>
          </div>

          <!-- Card Footer & Action Stepper -->
          <div class="order-card-footer">
            <div class="order-total-block">
              <span class="total-label">{{ $t('totale') || 'Totale' }}:</span>
              <span class="total-amount font-monospace">{{ (ordine.totale / 100).toFixed(2) }} €</span>
            </div>

            <!-- Status Control -->
            <div class="status-control-wrap">
              <div v-if="editingOrder === ordine._id" class="status-select-wrap">
                <select 
                  :value="ordine.stato" 
                  @change="updateStatus(ordine._id, $event.target.value)"
                  @blur="editingOrder = null"
                  class="sci-select-status"
                  ref="statusSelect"
                >
                  <option value="pagato">Nuovo (Pagato)</option>
                  <option value="in_preparazione">In Preparazione</option>
                  <option value="pronto">Pronto al Ritiro</option>
                  <option value="consegnato">Consegnato</option>
                </select>
              </div>
              
              <button 
                v-else 
                class="btn-status-pill" 
                :class="'status-btn-' + ordine.stato"
                @click="startEditing(ordine)"
                title="Fai clic per modificare lo stato dell'ordine"
              >
                <i class="fas" :class="getStatusIcon(ordine.stato)"></i>
                <span>{{ formatStatus(ordine.stato) }}</span>
                <i class="fas fa-pencil-alt edit-hint-icon"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Empty State -->
      <div v-else class="sci-empty-state">
        <div class="empty-content">
          <div class="empty-icon-halo">
            <i class="fas fa-motorcycle"></i>
          </div>
          <h3 class="empty-title">Nessun ordine trovato</h3>
          <p class="empty-desc">
            Non risultano ordini per la data e lo stato selezionati. Nuove ordinazioni compariranno qui automaticamente.
          </p>
          <button class="sci-btn sci-btn-primary mt-3" @click="goToCreateOrder">
            <i class="fas fa-plus me-1"></i> Crea Ordine Asporto
          </button>
        </div>
      </div>
    </main>

    <!-- Error/Notice Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="errorMessage">
        <i class="fas fa-info-circle me-2"></i>
        <span>{{ errorMessage }}</span>
      </div>
    </transition>

    <!-- Settings Modal -->
    <div class="modal-overlay" v-if="showSettings" @click.self="showSettings = false">
      <div class="sci-card settings-modal-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <h2 class="modal-title">
              <i class="fas fa-clock me-2 text-primary"></i>
              Impostazioni Orari Asporto
            </h2>
            <span class="sci-caption">Configura fasce di ritiro per pranzo e cena per ogni giorno</span>
          </div>
          <button class="btn-close-modal" @click="showSettings = false">&times;</button>
        </div>

        <div class="modal-body">
          <div class="sci-form-group mb-3">
            <label class="sci-label"><strong>Giorno della Settimana:</strong></label>
            <select v-model="selectedGiorno" class="sci-select">
              <option value="all">Tutti i giorni (Predefinito)</option>
              <option value="1">Lunedì</option>
              <option value="2">Martedì</option>
              <option value="3">Mercoledì</option>
              <option value="4">Giovedì</option>
              <option value="5">Venerdì</option>
              <option value="6">Sabato</option>
              <option value="0">Domenica</option>
            </select>
          </div>

          <div v-if="selectedGiorno !== 'all'" class="mb-3">
            <label class="sci-checkbox-label">
              <input type="checkbox" v-model="currentGiornoSetting.attivo" class="sci-checkbox" />
              <span>Asporto attivo il {{ getGiornoName(selectedGiorno) }}</span>
            </label>
          </div>

          <div class="settings-grid" :class="{ 'disabled-grid': selectedGiorno !== 'all' && !currentGiornoSetting.attivo }">
            <div class="setting-block">
              <h4 class="block-title"><i class="fas fa-sun me-1 text-warning"></i> Pranzo</h4>
              <div class="time-field-row">
                <label>Inizio:</label>
                <input type="time" v-model="currentGiornoSetting.asportoPranzoStart" class="sci-time-input">
              </div>
              <div class="time-field-row">
                <label>Fine:</label>
                <input type="time" v-model="currentGiornoSetting.asportoPranzoEnd" class="sci-time-input">
              </div>
            </div>

            <div class="setting-block">
              <h4 class="block-title"><i class="fas fa-moon me-1 text-primary"></i> Cena</h4>
              <div class="time-field-row">
                <label>Inizio:</label>
                <input type="time" v-model="currentGiornoSetting.asportoCenaStart" class="sci-time-input">
              </div>
              <div class="time-field-row">
                <label>Fine:</label>
                <input type="time" v-model="currentGiornoSetting.asportoCenaEnd" class="sci-time-input">
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="sci-btn sci-btn-secondary" @click="showSettings = false">Annulla</button>
          <button class="sci-btn sci-btn-primary" @click="saveSettings">
            <i class="fas fa-save me-1"></i> Salva Impostazioni
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import newOrderSound from '@/assets/newOrder.mp3'
const { ipcRenderer } = window.require ? window.require('electron') : { ipcRenderer: null };

export default {
  name: 'GestioneAsporto',
  data() {
    return {
      store,
      ordini: [],
      filtroStato: '',
      dataFiltro: new Date().toISOString().split('T')[0],
      searchQuery: '',
      isLoading: false,
      errorMessage: '',
      editingOrder: null,
      showSettings: false,
      selectedGiorno: 'all',
      asportoSettings: {
        codicesala: 'asporto',
        asportoPranzoStart: '12:00',
        asportoPranzoEnd: '15:00',
        asportoCenaStart: '19:00',
        asportoCenaEnd: '23:00',
        giorni: {
          '1': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '2': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '3': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '4': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '5': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '6': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '0': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' }
        }
      }
    }
  },
  computed: {
    currentGiornoSetting() {
      if (this.selectedGiorno === 'all') {
        return this.asportoSettings
      }
      const dayKey = String(this.selectedGiorno)
      const giorniMap = this.asportoSettings.giorni || {}
      return giorniMap[dayKey] || {
        attivo: true,
        asportoPranzoStart: this.asportoSettings.asportoPranzoStart || '12:00',
        asportoPranzoEnd: this.asportoSettings.asportoPranzoEnd || '15:00',
        asportoCenaStart: this.asportoSettings.asportoCenaStart || '19:00',
        asportoCenaEnd: this.asportoSettings.asportoCenaEnd || '23:00'
      }
    },
    filteredOrdini() {
      let list = this.ordini;
      if (this.filtroStato) {
        list = list.filter(o => o.stato === this.filtroStato);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(o => 
          (o.nome && o.nome.toLowerCase().includes(q)) ||
          (o.telefono && o.telefono.toLowerCase().includes(q)) ||
          (o.email && o.email.toLowerCase().includes(q)) ||
          (o.contatto && o.contatto.toLowerCase().includes(q))
        );
      }
      return list;
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Gestionale' })
    },
    goToCreateOrder() {
      this.$router.push({ name: 'OrdineAsporto', query: { mode: 'staff' } })
    },
    setStatusFilter(st) {
      this.filtroStato = st;
      this.fetchOrdini();
    },
    countByStatus(st) {
      return this.ordini.filter(o => o.stato === st).length;
    },
    getStatusIcon(st) {
      if (st === 'pagato') return 'fa-bell';
      if (st === 'in_preparazione') return 'fa-fire';
      if (st === 'pronto') return 'fa-check';
      if (st === 'consegnato') return 'fa-check-double';
      return 'fa-tag';
    },
    startEditing(ordine) {
      this.editingOrder = ordine._id
      this.$nextTick(() => {
        const selects = this.$refs.statusSelect
        if (selects) {
          const el = Array.isArray(selects) ? selects[0] : selects
          el?.focus()
        }
      })
    },
    formatVarianti(list) {
      if (!list || !list.length) return '';
      return list.map(item => {
        if (typeof item === 'string') return item;
        return item.descrizione || item.nome || item.prodottoId || '';
      }).filter(Boolean).join(', ');
    },
    async fetchOrdini() {
      this.isLoading = true;
      try {
        const payload = {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        }

        if (this.filtroStato) {
          payload.stato = this.filtroStato
        }

        if (this.dataFiltro) {
          payload.data = this.dataFiltro
        }

        const response = await this.axios.post(
          this.store.path + 'getOrdiniAsporto',
          payload
        )

        if (response.data && response.data.messaggio) {
          // Sort: active first, then pickup date/time
          this.ordini = response.data.messaggio.sort((a, b) => {
            if (a.stato === 'consegnato' && b.stato !== 'consegnato') return 1;
            if (a.stato !== 'consegnato' && b.stato === 'consegnato') return -1;

            const dateA = `${a.dataRitiro}T${a.oraRitiro}`;
            const dateB = `${b.dataRitiro}T${b.oraRitiro}`;
            return dateA.localeCompare(dateB);
          });
          this.printNewOrders();
        }
      } catch (error) {
        console.error('Error fetching orders:', error)
        this.errorMessage = 'Errore nel recupero degli ordini'
      } finally {
        this.isLoading = false;
      }
    },
    async updateStatus(id, nuovoStato) {
      try {
        await this.axios.post(this.store.path + 'aggiornaStatoAsporto', {
          id,
          stato: nuovoStato,
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        })
        
        this.editingOrder = null
        await this.fetchOrdini()
      } catch (error) {
        console.error('Error updating status:', error)
        this.errorMessage = 'Impossibile aggiornare lo stato'
        this.editingOrder = null
      }
    },
    async printNewOrders() {
      if (!ipcRenderer) return

      const today = new Date().toISOString().split('T')[0]
      const newOrders = this.ordini.filter(o => 
        !o.isPrinted && (
          (o.pagato && o.dataRitiro === today) || 
          o.isStaff
        )
      )

      if (newOrders.length === 0) return

      // Play sound
      try {
        const audio = new Audio(newOrderSound)
        audio.play().catch(e => console.warn('Audio play restricted:', e))
      } catch (e) {
        console.warn('Audio play error:', e)
      }

      try {
        const printerRes = await this.axios.post(this.store.path + 'displaySpecificPrinter', {
          nome: 'asporto'
        }, {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        })

        const printers = printerRes.data.messaggio
        if (!printers || !printers.length) {
          console.warn('No "asporto" printer found')
          return
        }

        const printer = printers[0]

        for (const ordine of newOrders) {
          console.log('Printing takeaway order:', ordine._id)
          
          const sanitizedOrder = JSON.parse(JSON.stringify({
            ord: ordine.ordinazione.map(item => ({
              ...item,
              piatto: item.piatto,
              aggiunta: item.aggiunta || [],
              senza: item.senza || [],
              prezzo: item.prezzo / 100,
              countGroup: item.quantita
            })),
            nome: ordine.nome,
            email: ordine.email,
            contatto: ordine.contatto || ordine.email,
            telefono: ordine.telefono,
            dataRitiro: ordine.dataRitiro,
            oraRitiro: ordine.oraRitiro,
            totale: ordine.totale / 100
          }))

          ipcRenderer.send('print', sanitizedOrder, { 
            ip: printer.ip, 
            porta: printer.porta 
          }, 'asporto')

          await this.axios.post(this.store.path + 'segnaAsportoStampato', {
            id: ordine._id
          }, {
            headers: {
              authorization: `Bearer ${JSON.parse(localStorage.token)}`
            }
          })
          
          ordine.isPrinted = true
        }
      } catch (error) {
        console.error('Error during automatic printing:', error)
      }
    },
    getGiornoName(dayKey) {
      const names = {
        '1': 'Lunedì',
        '2': 'Martedì',
        '3': 'Mercoledì',
        '4': 'Giovedì',
        '5': 'Venerdì',
        '6': 'Sabato',
        '0': 'Domenica'
      }
      return names[String(dayKey)] || ''
    },
    async fetchSettings() {
      try {
        const response = await this.axios.get(this.store.path + 'prenotazioni-setting', {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        })
        const setting = response.data.find(s => s.codicesala === 'asporto')
        if (setting) {
          const defaultStartP = setting.asportoPranzoStart || '12:00'
          const defaultEndP = setting.asportoPranzoEnd || '15:00'
          const defaultStartC = setting.asportoCenaStart || '19:00'
          const defaultEndC = setting.asportoCenaEnd || '23:00'
          const mergedGiorni = { ...this.asportoSettings.giorni, ...(setting.giorni || {}) }
          
          ;['0', '1', '2', '3', '4', '5', '6'].forEach(dayKey => {
            if (!mergedGiorni[dayKey]) {
              mergedGiorni[dayKey] = {
                attivo: true,
                asportoPranzoStart: defaultStartP,
                asportoPranzoEnd: defaultEndP,
                asportoCenaStart: defaultStartC,
                asportoCenaEnd: defaultEndC
              }
            }
          })

          this.asportoSettings = {
            codicesala: 'asporto',
            asportoPranzoStart: defaultStartP,
            asportoPranzoEnd: defaultEndP,
            asportoCenaStart: defaultStartC,
            asportoCenaEnd: defaultEndC,
            ...setting,
            giorni: mergedGiorni
          }
        }
      } catch (error) {
        console.error('Error fetching asporto settings:', error)
      }
    },
    async saveSettings() {
      try {
        await this.axios.post(this.store.path + 'prenotazioni-setting', this.asportoSettings, {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        })
        this.showSettings = false
        this.errorMessage = 'Impostazioni orari asporto salvate con successo!'
        setTimeout(() => { this.errorMessage = '' }, 3000)
      } catch (error) {
        console.error('Error saving asporto settings:', error)
        alert('Errore nel salvataggio delle impostazioni')
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatStatus(status) {
      if (status === 'pagato') return 'Nuovo (Pagato)';
      if (status === 'in_preparazione') return 'In Preparazione';
      if (status === 'pronto') return 'Pronto al Ritiro';
      if (status === 'consegnato') return 'Consegnato';
      return status;
    },
    formatDataRitiro(dateStr) {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
  },
  beforeMount() {
    this.fetchOrdini()
    this.fetchSettings()
    this.intervalId = setInterval(this.fetchOrdini, 30000)
  },
  beforeUnmount() {
    if (this.intervalId) clearInterval(this.intervalId)
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Main Content */
.sci-main-content {
  max-width: 1440px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Toolbar Card */
.sci-toolbar-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}

.status-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.status-pill {
  min-height: 40px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.88rem;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.status-pill:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.status-pill.active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.status-pill-pagato.active {
  background: #2563eb;
  border-color: #2563eb;
}

.status-pill-prep.active {
  background: #d97706;
  border-color: #d97706;
}

.status-pill-pronto.active {
  background: #059669;
  border-color: #059669;
}

.status-pill-consegnato.active {
  background: #64748b;
  border-color: #64748b;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.date-input-group, .search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.date-icon, .search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 0.9rem;
  pointer-events: none;
}

.sci-date-input {
  min-height: 40px;
  padding: 6px 12px 6px 36px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #ffffff;
}

.sci-search-input {
  min-height: 40px;
  padding: 6px 32px 6px 36px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #ffffff;
  min-width: 220px;
}

.clear-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 1rem;
}

/* Orders Grid */
.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}

@media (max-width: 600px) {
  .orders-grid {
    grid-template-columns: 1fr;
  }
}

/* Order Card */
.order-card {
  border-left: 5px solid #cbd5e1;
  border-radius: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
}

.order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px -4px rgba(0,0,0,0.08);
}

.card-status-pagato { border-left-color: #2563eb; }
.card-status-in_preparazione { border-left-color: #f59e0b; }
.card-status-pronto { border-left-color: #10b981; }
.card-status-consegnato { border-left-color: #94a3b8; opacity: 0.82; }

.order-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.customer-name {
  margin: 0 0 6px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.customer-contacts {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-pill {
  font-size: 0.82rem;
  color: #475569;
}

.order-header-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.badge-staff {
  background: #3b82f6;
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
}

.badge-unpaid {
  background: #ef4444;
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
}

.order-timestamp {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Pickup Banner */
.pickup-time-banner {
  background: #ecfdf5;
  color: #065f46;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  border-bottom: 1px solid #d1fae5;
}

.pickup-label {
  margin-right: 6px;
}

.pickup-datetime {
  font-weight: 700;
}

/* Items Box */
.order-items-box {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  max-height: 240px;
  overflow-y: auto;
}

.item-entry {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #f1f5f9;
}

.item-entry:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.item-main-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.item-qty {
  font-weight: 700;
  color: #0f172a;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.item-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
}

.item-extras {
  margin-left: 28px;
  font-size: 0.82rem;
}

.extra-add { color: #059669; }
.extra-rem { color: #dc2626; }

.item-note {
  margin-left: 28px;
  font-size: 0.82rem;
  color: #64748b;
  font-style: italic;
}

/* Card Footer */
.order-card-footer {
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-total-block {
  display: flex;
  flex-direction: column;
}

.total-label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.total-amount {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
}

/* Status Control Pill & Select */
.status-control-wrap {
  display: flex;
  align-items: center;
}

.btn-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-status-pill:hover {
  transform: scale(1.04);
}

.edit-hint-icon {
  font-size: 0.72rem;
  opacity: 0.6;
}

.status-btn-pagato { background: #eff6ff; color: #1d4ed8; }
.status-btn-in_preparazione { background: #fffbeb; color: #b45309; }
.status-btn-pronto { background: #ecfdf5; color: #047857; }
.status-btn-consegnato { background: #f1f5f9; color: #475569; }

.sci-select-status {
  min-height: 40px;
  padding: 6px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  font-size: 0.88rem;
  font-weight: 700;
  background: #ffffff;
  color: #0f172a;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.settings-modal-card {
  width: 100%;
  max-width: 540px;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
}

.btn-close-modal:hover {
  color: #0f172a;
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.sci-select {
  width: 100%;
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  background: #ffffff;
}

.sci-checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  cursor: pointer;
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 10px;
}

.setting-block {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.block-title {
  margin: 0 0 6px 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}

.time-field-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.88rem;
  color: #475569;
}

.sci-time-input {
  min-height: 36px;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.88rem;
}

.disabled-grid {
  opacity: 0.4;
  pointer-events: none;
}

.modal-footer {
  padding: 16px 24px;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Floating Toast */
.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #0f172a;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 30px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.25);
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 1100;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.sci-btn-secondary {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* Empty State */
.sci-empty-state {
  padding: 80px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 440px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 18px;
}

.empty-title {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.92rem;
  color: #64748b;
  line-height: 1.5;
}
</style>
